import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

export const Profile = () => {
  const currentUser = useSelector(state => state.user.currentUser);
  const dispatch = useDispatch();

  // Profile fields
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    job_title: '',
    address: '',
    phone: ''
  });
  const [editProfile, setEditProfile] = useState({ ...profile });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  // Password fields
  const [isUpdating, setIsUpdating] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetch('/apiprofile', { credentials: 'include' })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) {
          setProfile(data);
          setEditProfile(data);
        }
      });
  }, []);

  const handleSaveProfile = async () => {
    setProfileError('');
    setProfileSuccess('');
    try {
      const res = await fetch('/apiupdateProfile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editProfile)
      });
      if (!res.ok) {
        const data = await res.json();
        setProfileError(data.message || 'Failed to update profile');
        return;
      }
      const data = await res.json();
      setProfile(data);
      dispatch({ type: 'UPDATE_PROFILE', payload: data });
      setProfileSuccess('Profile updated successfully!');
      setIsEditingProfile(false);
    } catch (err) {
      setProfileError('An error occurred. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditProfile({ ...profile });
    setIsEditingProfile(false);
    setProfileError('');
    setProfileSuccess('');
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newPassword || !confirmPassword) {
      setError('Please enter both password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      const response = await fetch('/apiupdatePassword', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ newPassword })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Password updated successfully!');
        setNewPassword('');
        setConfirmPassword('');
        setIsUpdating(false);
      } else {
        setError(data.message || 'Failed to update password');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    }
  };

  const profileFields = [
    { key: 'first_name', label: 'First Name' },
    { key: 'last_name', label: 'Last Name' },
    { key: 'job_title', label: 'Job Title' },
    { key: 'address', label: 'Address' },
    { key: 'phone', label: 'Phone Number' }
  ];

  return (
    <div className="location">
      <div className="locationDetailsWrapper">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>Profile</h2>
          {!isEditingProfile && (
            <button className="mainButton" onClick={() => { setIsEditingProfile(true); setProfileSuccess(''); }}>
              Edit Profile
            </button>
          )}
        </div>

        <div style={{ marginBottom: '24px' }}>
          <p className="fieldLabel">Email</p>
          <p className="field">{currentUser?.email || 'Not available'}</p>
        </div>

        {profileSuccess && (
          <div style={{ padding: '12px', marginBottom: '16px', backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '4px' }}>
            {profileSuccess}
          </div>
        )}

        {profileError && (
          <div style={{ padding: '12px', marginBottom: '16px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px' }}>
            {profileError}
          </div>
        )}

        {isEditingProfile ? (
          <div style={{ marginBottom: '24px' }}>
            {profileFields.map(({ key, label }) => (
              <div key={key} style={{ marginBottom: '12px' }}>
                <label className="fieldLabel">{label}</label>
                <input
                  className="inputAuth"
                  value={editProfile[key]}
                  onChange={e => setEditProfile({ ...editProfile, [key]: e.target.value })}
                  placeholder={label}
                  style={{ width: '100%', maxWidth: '400px', display: 'block' }}
                />
              </div>
            ))}
            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
              <button className="mainButton" onClick={handleSaveProfile}>Save Profile</button>
              <button className="mainButton" style={{ backgroundColor: '#999' }} onClick={handleCancelEdit}>Cancel</button>
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: '24px' }}>
            {profileFields.map(({ key, label }) => (
              <div key={key} style={{ marginBottom: '12px' }}>
                <p className="fieldLabel">{label}</p>
                <p className="field">{profile[key] || '-'}</p>
              </div>
            ))}
          </div>
        )}

        <hr style={{ border: 'none', borderTop: '1px solid #ddd', margin: '24px 0' }} />

        <div style={{ marginBottom: '24px' }}>
          <p className="fieldLabel">Password</p>
          <p className="field">••••••••</p>
        </div>

        {!isUpdating ? (
          <button
            onClick={() => setIsUpdating(true)}
            className="mainButton"
          >
            Update Password
          </button>
        ) : (
          <div style={{ marginTop: '24px', padding: '20px', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f9f9f9' }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px' }}>Update Password</h3>

            {error && (
              <div style={{ padding: '12px', marginBottom: '16px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px' }}>
                {error}
              </div>
            )}

            {success && (
              <div style={{ padding: '12px', marginBottom: '16px', backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '4px' }}>
                {success}
              </div>
            )}

            <form onSubmit={handleUpdatePassword}>
              <div style={{ marginBottom: '16px' }}>
                <label className="fieldLabel">New Password</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="inputAuth"
                    placeholder="Enter new password"
                    style={{ width: '100%', maxWidth: '400px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="mainButton"
                    style={{ padding: '6px 12px', fontSize: '14px', minWidth: '70px' }}
                  >
                    {showNewPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label className="fieldLabel">Confirm New Password</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="inputAuth"
                    placeholder="Confirm new password"
                    style={{ width: '100%', maxWidth: '400px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="mainButton"
                    style={{ padding: '6px 12px', fontSize: '14px', minWidth: '70px' }}
                  >
                    {showConfirmPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="submit" className="mainButton">
                  Save Password
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsUpdating(false);
                    setNewPassword('');
                    setConfirmPassword('');
                    setShowNewPassword(false);
                    setShowConfirmPassword(false);
                    setError('');
                    setSuccess('');
                  }}
                  className="mainButton"
                  style={{ backgroundColor: '#999' }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};
